import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import './Login.css'
import api from '../config/api'
import {
  clearStoredUser,
  getDashboardForRole,
  getStoredUser,
  getUserDisplayName,
  persistAuth,
} from '../config/authUtils'

const Lockscreen = () => {
  const navigate = useNavigate()
  const user = getStoredUser()
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  // 2FA State
  const [twoFARequired, setTwoFARequired] = useState(false)
  const [otpCode, setOtpCode] = useState('')
  const [otpError, setOtpError] = useState('')
  const [otpSuccessMessage, setOtpSuccessMessage] = useState('')
  const [otpLoading, setOtpLoading] = useState(false)
  const [resendLoading, setResendLoading] = useState(false)

  useEffect(() => {
    if (!getStoredUser()) {
      navigate('/login', { replace: true })
    }
  }, [navigate])

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!user?.email) {
      navigate('/login', { replace: true })
      return
    }

    setLoading(true)
    setError(null)

    try {
      const response = await api.post('/api/login/', {
        email: user.email,
        password,
      })

      if (response.data?.['2fa_required']) {
        setTwoFARequired(true)
        setOtpCode('')
        setOtpError('')
        setOtpSuccessMessage(
          response.data?.detail ||
            'Two-factor authentication code sent to your email address. It will expire in 10 minutes.'
        )
        return
      }

      persistAuth(response.data)

      const storedAfter = { ...(getStoredUser() || {}), ...(response.data?.user || {}) }
      const redirect = getDashboardForRole(storedAfter.role || response.data?.user?.role)
      navigate(redirect || '/', { replace: true })
    } catch (err) {
      console.error('Unlock failed', err)
      setError(
        err.response?.data?.detail ||
          err.response?.data?.message ||
          err.response?.data?.non_field_errors?.[0] ||
          'Incorrect password. Please try again.'
      )
    } finally {
      setLoading(false)
    }
  }

  const handleVerify2FA = async (e) => {
    e.preventDefault()
    setOtpError('')
    setOtpSuccessMessage('')
    setOtpLoading(true)

    try {
      const response = await api.post('/api/login/verify-2fa/', {
        email: user.email,
        otp_code: otpCode,
      })

      persistAuth(response.data)

      const storedAfter = { ...(getStoredUser() || {}), ...(response.data?.user || {}) }
      const redirect = getDashboardForRole(storedAfter.role || response.data?.user?.role)
      navigate(redirect || '/', { replace: true })
    } catch (err) {
      console.error('2FA verification failed', err)
      const errorMsg =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        'Invalid or expired 2FA code.'
      setOtpError(errorMsg)
    } finally {
      setOtpLoading(false)
    }
  }

  const handleResend2FA = async () => {
    setOtpError('')
    setOtpSuccessMessage('')
    setResendLoading(true)

    try {
      const response = await api.post('/api/login/resend-2fa/', {
        email: user.email,
      })
      setOtpSuccessMessage(
        response.data?.detail ||
          'A new 2FA code has been sent to your email address if the account exists.'
      )
    } catch (err) {
      console.error('Failed to resend 2FA', err)
      const errorMsg =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        'Failed to resend 2FA code.'
      setOtpError(errorMsg)
    } finally {
      setResendLoading(false)
    }
  }

  const handleSwitchAccount = () => {
    clearStoredUser()
    navigate('/login', { replace: true })
  }

  if (!user) {
    return null
  }

  const initial = (getUserDisplayName(user) || user.email || 'U')
    .charAt(0)
    .toUpperCase()

  return (
    <div className="login-page">
      <div className="background-shapes">
        <div className="shape shape-1"></div>
        <div className="shape shape-2"></div>
        <div className="shape shape-4"></div>
        <div className="shape shape-5"></div>
        <div className="shape shape-6"></div>
        <div className="shape shape-7"></div>
        <div className="shape shape-8"></div>
        <div className="shape shape-9"></div>
      </div>

      <div className="login-container">
        <div className="logo-container mb-4">
          <img
            src="assets/img/media/celebration-logo.png"
            alt="Company Logo"
            className="company-logo"
            onError={(e) => {
              e.target.style.display = 'none'
              const logoText = document.querySelector('.logo-text')
              if (logoText) logoText.style.display = 'block'
            }}
          />
          <h2 className="logo-text" style={{ display: 'none' }}>
            PMS
          </h2>
        </div>

        <div className="text-center mb-4">
          <div className="mb-3 d-flex justify-content-center">
            <span
              className="avatar rounded-circle flex-shrink-0 d-flex align-items-center justify-content-center fw-bold text-white"
              style={{
                width: '80px',
                height: '80px',
                overflow: 'hidden',
                border: '3px solid rgba(255, 255, 255, 0.2)',
                boxShadow: '0 8px 16px rgba(0,0,0,0.3)',
                backgroundColor: '#4F46E5',
                fontSize: '1.8rem',
              }}
            >
              <img
                src="/assets/img/avatar101.jpg"
                className="rounded-circle w-100 h-100 position-absolute top-0 start-0"
                style={{ objectFit: 'cover' }}
                alt={getUserDisplayName(user)}
                onError={(e) => {
                  e.target.style.display = 'none'
                }}
              />
              {initial}
            </span>
          </div>
          <h5 className="text-white fw-bold mb-1" style={{ fontSize: '18px' }}>
            {getUserDisplayName(user)}
          </h5>
          <p
            style={{
              color: 'rgba(255, 255, 255, 0.65)',
              fontSize: '14px',
              margin: 0,
            }}
          >
            {user.email}
          </p>
        </div>

        {!twoFARequired ? (
          <form onSubmit={handleSubmit} className="login-form">
            {error && (
            <div
              className="alert alert-danger border-0 mb-4 shadow-sm fs-14"
              role="alert"
            >
              <div className="d-flex align-items-center gap-2">
                <i className="ti ti-alert-triangle fs-18 flex-shrink-0" />
                <span>{error}</span>
              </div>
            </div>
          )}

          <div className="form-group mb-3" style={{ marginTop: '15px' }}>
            <label
              htmlFor="lockscreen-password"
              className="form-label fs-13 fw-medium mb-2"
              style={{ color: 'rgba(255,255,255,0.85)' }}
            >
              Password
            </label>
            <div className="input-container position-relative">
              <span
                className="position-absolute top-50 translate-middle-y d-flex align-items-center justify-content-center"
                style={{ left: '14px', color: 'rgba(255,255,255,0.55)' }}
              >
                <i className="ti ti-lock fs-18" />
              </span>
              <input
                id="lockscreen-password"
                type={showPassword ? 'text' : 'password'}
                className="form-control form-control-lg fs-14"
                style={{
                  paddingLeft: '46px',
                  paddingRight: '48px',
                  backgroundColor: 'rgba(255,255,255,0.07)',
                  border: '1px solid rgba(255,255,255,0.12)',
                  color: '#fff',
                  borderRadius: '10px',
                }}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                autoFocus
                autoComplete="current-password"
              />
              <button
                type="button"
                className="btn btn-link position-absolute top-50 end-0 translate-middle-y text-decoration-none p-0 me-3 d-flex align-items-center justify-content-center"
                onClick={() => setShowPassword((prev) => !prev)}
                style={{ color: 'rgba(255,255,255,0.6)' }}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                <i
                  className={`ti ${
                    showPassword ? 'ti-eye-off' : 'ti-eye'
                  } fs-18`}
                />
              </button>
            </div>
          </div>

          <button
            type="submit"
            className="btn w-100 py-3 fw-bold fs-15 shadow-lg"
            disabled={loading}
            style={{
              marginTop: '25px',
              background: 'linear-gradient(135deg, #4F46E5 0%, #6366F1 100%)',
              border: 'none',
              color: '#fff',
              borderRadius: '10px',
              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            }}
            onMouseOver={(e) => {
              if (!loading) {
                e.currentTarget.style.transform = 'translateY(-1px)'
                e.currentTarget.style.boxShadow =
                  '0 10px 24px rgba(79, 70, 229, 0.35)'
              }
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'translateY(0)'
              e.currentTarget.style.boxShadow =
                '0 8px 16px rgba(0, 0, 0, 0.15)'
            }}
          >
            {loading ? (
              <span className="d-flex align-items-center justify-content-center gap-2">
                <span
                  className="spinner-border spinner-border-sm text-white"
                  role="status"
                  aria-hidden="true"
                />
                <span>Unlocking...</span>
              </span>
            ) : (
              <span className="d-flex align-items-center justify-content-center gap-2">
                <i className="ti ti-lock-open fs-18" />
                Unlock
              </span>
            )}
          </button>

          <div style={{ marginTop: '20px', textAlign: 'center' }}>
            <button
              type="button"
              className="btn btn-link p-0 text-decoration-none fs-13 fw-medium"
              onClick={handleSwitchAccount}
              style={{ color: '#a5b4fc' }}
            >
              <i className="ti ti-user-switch fs-16 me-1 align-middle" />
              Sign in as a different user
            </button>
          </div>
          </form>
        ) : (
          <form onSubmit={handleVerify2FA} className="login-form">
            <div className="text-center mb-4">
              <div
                className="avatar avtar-lg bg-indigo mx-auto mb-3 d-flex align-items-center justify-content-center rounded-circle shadow"
                style={{ width: '60px', height: '60px', backgroundColor: '#4F46E5' }}
              >
                <i className="ti ti-shield-check fs-28 text-white" />
              </div>
              <h4 className="fw-bold text-white mb-1">Two-Factor Authentication</h4>
              <p className="fs-13 text-light opacity-75 mb-0">
                Enter the 6-digit code sent to <strong className="text-white">{user.email}</strong>
              </p>
            </div>

            {otpSuccessMessage && (
              <div
                className="alert alert-success border-0 mb-4 shadow-sm fs-13"
                role="alert"
              >
                <div className="d-flex align-items-center gap-2">
                  <i className="ti ti-circle-check fs-18 flex-shrink-0" />
                  <span>{otpSuccessMessage}</span>
                </div>
              </div>
            )}

            {otpError && (
              <div
                className="alert alert-danger border-0 mb-4 shadow-sm fs-13"
                role="alert"
              >
                <div className="d-flex align-items-center gap-2">
                  <i className="ti ti-alert-triangle fs-18 flex-shrink-0" />
                  <span>{otpError}</span>
                </div>
              </div>
            )}

            <div className="form-group mb-4">
              <label
                htmlFor="otp_code"
                className="form-label fs-13 fw-medium mb-2"
                style={{ color: 'rgba(255,255,255,0.85)' }}
              >
                Verification Code
              </label>
              <div className="input-container position-relative">
                <span
                  className="position-absolute top-50 translate-middle-y d-flex align-items-center justify-content-center"
                  style={{ left: '14px', color: 'rgba(255,255,255,0.55)' }}
                >
                  <i className="ti ti-key fs-18" />
                </span>
                <input
                  type="text"
                  id="otp_code"
                  name="otp_code"
                  className="form-control form-control-lg fs-16 text-center fw-bold letter-spacing-2"
                  style={{
                    paddingLeft: '46px',
                    backgroundColor: 'rgba(255,255,255,0.07)',
                    border: '1px solid rgba(255,255,255,0.12)',
                    color: '#fff',
                    borderRadius: '10px',
                    letterSpacing: '6px',
                  }}
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value)}
                  placeholder="123456"
                  maxLength={6}
                  autoComplete="one-time-code"
                  required
                  autoFocus
                />
              </div>
            </div>

            <button
              type="submit"
              className="btn w-100 py-3 fw-bold fs-15 shadow-lg mb-3"
              disabled={otpLoading || otpCode.length < 6}
              style={{
                background: 'linear-gradient(135deg, #4F46E5 0%, #6366F1 100%)',
                border: 'none',
                color: '#fff',
                borderRadius: '10px',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                opacity: otpCode.length < 6 ? 0.7 : 1,
              }}
            >
              {otpLoading ? (
                <span className="d-flex align-items-center justify-content-center gap-2">
                  <span
                    className="spinner-border spinner-border-sm text-white"
                    role="status"
                    aria-hidden="true"
                  />
                  <span>Verifying Code...</span>
                </span>
              ) : (
                <span className="d-flex align-items-center justify-content-center gap-2">
                  <i className="ti ti-shield-check fs-18" />
                  Verify &amp; Continue
                </span>
              )}
            </button>

            <div className="d-flex align-items-center justify-content-between pt-2">
              <button
                type="button"
                className="btn btn-link p-0 text-decoration-none fs-13 text-light opacity-75"
                onClick={() => setTwoFARequired(false)}
              >
                <i className="ti ti-arrow-left me-1" />
                Back to Login
              </button>

              <button
                type="button"
                className="btn btn-link p-0 text-decoration-none fs-13 fw-medium"
                onClick={handleResend2FA}
                disabled={resendLoading}
                style={{ color: '#a5b4fc' }}
              >
                {resendLoading ? (
                  <span>Resending code...</span>
                ) : (
                  <span>Resend Code</span>
                )}
              </button>
            </div>
            
            <div style={{ marginTop: '20px', textAlign: 'center' }}>
              <button
                type="button"
                className="btn btn-link p-0 text-decoration-none fs-13 fw-medium"
                onClick={handleSwitchAccount}
                style={{ color: '#a5b4fc' }}
              >
                <i className="ti ti-user-switch fs-16 me-1 align-middle" />
                Sign in as a different user
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}

export default Lockscreen
